// Compliance hub: every statement, its status, and what it is attached to.
//
// Deliberately a separate page rather than another tab on the customer record.
// A compliance statement belongs to a TRANSACTION as much as to a company —
// "which statements are outstanding right now" is the question an operator
// actually asks, and it cuts across customers.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { FileCheck, Mail, Plus, ShieldCheck } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { EusEditor } from '@/components/eus-editor';
import { ComplianceProfilePanel } from '@/components/compliance-profile-panel';
import { NewStatementModal } from '@/components/new-statement-modal';
import {
  ensureStandardEusTemplate, listComplianceRequests, listComplianceSubmissions,
  listComplianceTemplates, listTradingPartners,
  type ComplianceRequestStatus, type ComplianceSubmissionStatus,
} from '@/lib/api';

const REQUEST_PILL: Record<ComplianceRequestStatus, string> = {
  draft: 'pill-default',
  active: 'pill-info',
  completed: 'pill-success',
  revoked: 'pill-default',
  expired: 'pill-warning',
  cancelled: 'pill-default',
};

const STATUS_PILL: Record<ComplianceSubmissionStatus, string> = {
  draft: 'pill-default',
  submitted: 'pill-info',
  under_review: 'pill-info',
  changes_requested: 'pill-warning',
  accepted: 'pill-success',
  superseded: 'pill-default',
  void: 'pill-default',
};

/** Statuses that still need somebody to do something. */
const OPEN: ComplianceSubmissionStatus[] = ['draft', 'submitted', 'under_review', 'changes_requested'];

export function CompliancePage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [openSubmission, setOpenSubmission] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [tab, setTab] = useState<'statements' | 'requests' | 'companies'>('statements');
  const [newStatement, setNewStatement] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);

  const { data: subs, isLoading } = useQuery({
    queryKey: ['compliance-submissions'],
    queryFn: () => listComplianceSubmissions(() => getToken()),
  });

  const { data: partners } = useQuery({
    queryKey: ['trading-partners'],
    queryFn: () => listTradingPartners(() => getToken()),
    enabled: tab === 'companies',
  });

  const { data: requests } = useQuery({
    queryKey: ['compliance-requests'],
    queryFn: () => listComplianceRequests(() => getToken()),
  });

  const { data: templates } = useQuery({
    queryKey: ['compliance-templates'],
    queryFn: () => listComplianceTemplates(() => getToken()),
  });

  const ensureTemplate = useMutation({
    mutationFn: () => ensureStandardEusTemplate(() => getToken()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance-templates'] }),
  });

  const rows = subs?.submissions ?? [];
  const requestRows = requests?.requests ?? [];
  const awaitingCustomer = requestRows.filter((r) => r.status === 'active').length;
  /** Statements the customer has signed and nobody has reviewed yet. */
  const awaitingReview = rows.filter((r) => r.status === 'submitted').length;
  const visible = showAll ? rows : rows.filter((r) => OPEN.includes(r.status));
  const openCount = rows.filter((r) => OPEN.includes(r.status)).length;

  return (
    <AppShell
      title="Compliance"
      subtitle="End-use statements and counterparty compliance records"
      headerActions={
        tab === 'statements' ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAll(!showAll)} className="btn-ghost text-xs">
              {showAll ? 'Show open only' : `Show all (${rows.length})`}
            </button>
            {/* The first step of the workflow. Until this existed there was no
                way to begin a statement from the UI at all — see
                components/new-statement-modal.tsx. Hidden with no published
                template, because there would be nothing to choose. */}
            <button
              onClick={() => setNewStatement(true)}
              disabled={!templates || templates.templates.length === 0}
              className="btn-primary text-xs"
              title={templates && templates.templates.length === 0
                ? 'Publish the standard EUS template first'
                : 'Start a compliance statement'}
            >
              <Plus className="w-3.5 h-3.5" aria-hidden />
              New statement
            </button>
          </div>
        ) : null
      }
    >
      {/* Two views, because the questions are different: "what is outstanding
          right now" cuts across companies, while "is this company's record
          complete" is about one of them. */}
      <div className="flex items-center gap-1 mb-4 border-b border-border">
        {(['statements', 'requests', 'companies'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
              tab === t
                ? 'border-primary text-foreground'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            {t === 'statements' ? 'Statements'
              : t === 'requests' ? `Customer requests${awaitingCustomer > 0 ? ` (${awaitingCustomer})` : ''}`
              : 'Company records'}
          </button>
        ))}
      </div>

      {tab === 'companies' && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div className="card">
            <div className="px-3 py-2 border-b border-border">
              <h3 className="text-xs font-semibold">Companies</h3>
            </div>
            {!partners ? (
              <p className="px-3 py-4 text-xs text-foreground-muted">Loading…</p>
            ) : partners.tradingPartners.length === 0 ? (
              <p className="px-3 py-4 text-xs text-foreground-muted">
                No trading partners yet. Partner records are created by onboarding or by the
                backfill; until then there is no company record to hold a compliance profile.
              </p>
            ) : (
              <ul className="max-h-[60dvh] overflow-auto">
                {partners.tradingPartners.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => setSelectedPartner(p.id)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-border hover:bg-surface-muted ${
                        selectedPartner === p.id ? 'bg-surface-muted font-medium' : ''
                      }`}
                    >
                      <span className="block truncate">{p.legalName}</span>
                      <span className="text-[11px] text-foreground-muted">
                        {p.roles.customer && 'customer'}
                        {p.roles.customer && p.roles.vendor && ' · '}
                        {p.roles.vendor && 'vendor'}
                        {p.countryCode && ` · ${p.countryCode}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="min-w-0">
            {selectedPartner ? (
              <ComplianceProfilePanel partnerPublicId={selectedPartner} />
            ) : (
              <div className="card p-6 text-xs text-foreground-muted">
                Select a company to see its compliance profile.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div className="card">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Mail className="w-4 h-4 text-foreground-muted" aria-hidden />
            <h3 className="text-sm font-semibold">Customer requests</h3>
            {awaitingCustomer > 0 && (
              <span className="pill pill-info">{awaitingCustomer} awaiting the customer</span>
            )}
          </div>
          {requestRows.length === 0 ? (
            <p className="px-4 py-6 text-xs text-foreground-muted">
              No customer requests yet. Open a draft statement and send a secure link from there.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:min-w-[760px]">
                  <thead>
                    <tr className="text-foreground-muted text-left border-b border-border">
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Recipient</th>
                      <th className="hidden px-4 py-2 font-medium md:table-cell">Sent</th>
                      <th className="hidden px-4 py-2 font-medium md:table-cell">Expires</th>
                      <th className="hidden px-4 py-2 font-medium md:table-cell">Link fetched</th>
                      <th className="px-4 py-2 font-medium">Customer started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestRows.map((r) => (
                      <tr
                        key={r.publicId}
                        onClick={() => setOpenSubmission(r.submissionPublicId)}
                        className="border-b border-border hover:bg-surface-muted cursor-pointer"
                      >
                        <td className="px-4 py-2">
                          <span className={`pill ${REQUEST_PILL[r.status]}`}>{r.status}</span>
                        </td>
                        <td className="px-4 py-2 min-w-0">
                          <span className="block max-w-[200px] truncate md:max-w-[240px]">
                            {r.recipientName ? `${r.recipientName} · ` : ''}{r.recipientEmail}
                          </span>
                          {/* The columns hidden on a phone, folded under the name. */}
                          <span className="block text-foreground-muted md:hidden">
                            Sent {r.issuedAt ? new Date(r.issuedAt).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td className="hidden px-4 py-2 text-foreground-muted tabular-nums md:table-cell">
                          {r.issuedAt ? new Date(r.issuedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="hidden px-4 py-2 text-foreground-muted tabular-nums md:table-cell">
                          {r.expiresAt ? new Date(r.expiresAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="hidden px-4 py-2 text-foreground-muted tabular-nums md:table-cell">
                          {r.firstFetchedAt ? new Date(r.firstFetchedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-2 text-foreground-muted tabular-nums">
                          {r.humanStartedAt ? new Date(r.humanStartedAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-3 text-[11px] text-foreground-muted border-t border-border">
                <span className="font-medium">&ldquo;Link fetched&rdquo; is not the customer.</span>{' '}
                Mail security scanners retrieve every link in an email before anyone reads it, so it
                often fills in within seconds of sending. &ldquo;Customer started&rdquo; needs a
                deliberate action by a person.
              </p>
            </>
          )}
        </div>
      )}

      {templates && templates.customerWorkflowEnabled === false && tab !== 'companies' && (
        <div className="card p-3 mb-4 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-foreground-muted mt-0.5 flex-shrink-0" aria-hidden />
          <p className="text-xs">
            <span className="font-medium">Sending statements to customers is switched off.</span>{' '}
            You can create, complete, review and accept statements here as normal &mdash; that is
            exactly what this mode is for. Issuing a secure link to a counterparty is enabled
            deliberately, once company records are in place and the workflow has been checked
            end to end.
          </p>
        </div>
      )}

      <div className={tab === 'statements' ? 'space-y-4' : 'hidden'}>
        {awaitingReview > 0 && (
          <div className="card p-3 border-[hsl(var(--info-muted))] flex items-start gap-2">
            <FileCheck className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
            <p className="text-xs">
              <span className="font-medium">
                {awaitingReview} statement{awaitingReview === 1 ? '' : 's'} submitted — review required.
              </span>{' '}
              Open one to compare what was declared against the company record, then accept it or
              ask for changes.
            </p>
          </div>
        )}
        {/* No template published yet — the one setup step. */}
        {templates && templates.templates.length === 0 && (
          <div className="card p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold">No statement template published yet</h3>
                <p className="text-xs text-foreground-muted mt-1">
                  Publish the standard End-Use Statement to start issuing statements. It collects
                  what supplier forms ask for, so one completed statement can be mapped onto a
                  supplier&apos;s own document instead of collecting the same facts again per vendor.
                </p>
                <button
                  onClick={() => ensureTemplate.mutate()}
                  disabled={ensureTemplate.isPending}
                  className="btn-primary text-xs mt-3"
                >
                  <Plus className="w-3.5 h-3.5" aria-hidden />
                  {ensureTemplate.isPending ? 'Publishing…' : 'Publish standard EUS'}
                </button>
                {ensureTemplate.isError && (
                  <p className="text-xs text-destructive mt-2">
                    {(ensureTemplate.error as { message?: string })?.message ?? 'Could not publish'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {templates && templates.templates.length > 0 && (
          <div className="card p-3">
            <h3 className="text-xs font-semibold mb-2">Available templates</h3>
            <ul className="flex flex-wrap gap-2">
              {templates.templates.map((t) => (
                <li key={t.versionId} className="text-[11px]">
                  <span className="pill pill-default">
                    {t.code} {t.versionLabel}
                  </span>
                  <span className="text-foreground-muted ml-1.5">
                    {t.name} · {t.ownerKind === 'platform' ? 'platform' : 'yours'} · {t.scope.replace(/_/g, ' ')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-foreground-muted mt-2">
              <span className="font-medium">New statement</span> starts one against a company. The
              company&apos;s own record supplies the suggestions; the customer confirms or corrects
              them. Attaching a statement to a specific RFQ line — which would prefill the part,
              quantity and serials from the transaction — is not wired into this screen yet.
            </p>
          </div>
        )}

        <div className="card">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-foreground-muted" aria-hidden />
            <h3 className="text-sm font-semibold">Statements</h3>
            {openCount > 0 && (
              <span className="pill pill-warning">{openCount} open</span>
            )}
          </div>

          {isLoading ? (
            <p className="px-4 py-6 text-xs text-foreground-muted">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="px-4 py-6 text-xs text-foreground-muted">
              {rows.length === 0
                ? 'No statements yet.'
                : 'Nothing open. Everything is accepted or superseded.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs md:min-w-[720px]">
                <thead>
                  <tr className="text-foreground-muted text-left border-b border-border">
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Company</th>
                    <th className="hidden px-4 py-2 font-medium md:table-cell">Form</th>
                    <th className="hidden px-4 py-2 font-medium md:table-cell">Scope</th>
                    <th className="px-4 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr
                      key={s.publicId}
                      onClick={() => setOpenSubmission(s.publicId)}
                      className="border-b border-border hover:bg-surface-muted cursor-pointer"
                    >
                      <td className="px-4 py-2">
                        <span className={`pill ${STATUS_PILL[s.status]}`}>
                          {s.status.replace(/_/g, ' ')}
                        </span>
                        {s.revision > 1 && (
                          <span className="pill pill-default ml-1">rev {s.revision}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 min-w-0">
                        <span className="block max-w-[200px] truncate md:max-w-[220px]">{s.partnerLegalName}</span>
                        {/* Form and scope, folded under the name on a phone. */}
                        <span className="block text-foreground-muted md:hidden">
                          {s.templateCode} {s.versionLabel} · {s.scope.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="hidden px-4 py-2 text-foreground-muted md:table-cell">
                        {s.templateCode} {s.versionLabel}
                      </td>
                      <td className="hidden px-4 py-2 text-foreground-muted md:table-cell">
                        {s.scope.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-2 text-foreground-muted tabular-nums">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {newStatement && templates && (
        <NewStatementModal
          templates={templates.templates}
          onClose={() => setNewStatement(false)}
          onCreated={(publicId) => {
            setNewStatement(false);
            // Straight into the editor: a draft nobody opens is a draft nobody
            // finishes, and the operator's next action is always to fill it in.
            setOpenSubmission(publicId);
          }}
        />
      )}

      {openSubmission && (
        <EusEditor
          submissionPublicId={openSubmission}
          onClose={() => setOpenSubmission(null)}
        />
      )}
    </AppShell>
  );
}
